import { useEffect, useState } from "react";
import useUser from "../Store/Context/UserContext";
import decodeToken from "../lib/decodeToken";
import { API_BASE_URL, getUserDetails } from "../Store/apiStore";

/*
 * Header ka "Hello! <naam>" greeting — /fast-agent-detail aur /dashboard dono
 * yahi use karte hain, taaki dono jagah bilkul ek jaisa behave kare.
 * (Pehle ye logic sirf FastAgentDetails ke andar tha.)
 *
 * Kya deta hai:
 *   • headerFirstName  — sirf pehla naam ("Nitish Pathania" → "Nitish")
 *   • showCompletePrompt — profile adhoora ho (naam / photo / phone) to har
 *                          4 second pe true/false hota hai; header naam aur
 *                          NUDGE_TEXT ke beech swap karta hai
 *   • profileImg       — DB wali taaza photo, warna store / JWT wali
 *
 * Profile-picture hydration safety net:
 * persisted zustand `user` store me kabhi `profile` field nahi hota (purane
 * setUser({name}) calls ne poora object replace kar diya, ya pehli baar ka
 * user). Mount pe agar JWT me profile URL hai aur store me nahi, to copy kar
 * dete hain — "avatar ek pal dikh ke gayab" wala flicker isi se band hua.
 */

export const NUDGE_TEXT = "Update Profile";
// Har 4 second pe naam <-> nudge swap hota hai. "Hello!" beech me se hatta
// nahi — sirf neeche wali line badalti hai.
const NUDGE_INTERVAL_MS = 4000;

const resolveProfileUrl = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    if (/^https?:\/\//i.test(raw) && !raw.includes("public")) return raw;
    const apiOrigin = String(API_BASE_URL || "").split("/api")[0];
    if (raw.includes("public")) {
        const tail = raw.split("public")[1];
        return tail && apiOrigin ? `${apiOrigin}${tail}` : raw;
    }
    if (raw.startsWith("/") && apiOrigin) return `${apiOrigin}${raw}`;
    return raw;
};

const useProfileGreeting = () => {
    const { user, updateUser } = useUser();
    const token = localStorage.getItem("token") || "";
    const decodeTokenData = decodeToken(token);
    const userIdFromToken = decodeTokenData?.id;

    /*
     * Do cheezein chahiye jo user store nahi deta: DB ka naam aur phone.
     * `loaded` isliye hai taaki fetch aane se pehle hum galti se "profile
     * adhoora hai" na maan lein — warna har page load pe ek jhalak nudge ki
     * dikhti, chahe profile poora ho.
     */
    const [profileFields, setProfileFields] = useState({
        loaded: false,
        name: "",
        phone: "",
        profile: "",
    });
    // Shuruaat greeting se hoti hai, taaki landing pe hi taunt na lage.
    const [showCompletePrompt, setShowCompletePrompt] = useState(false);

    /*
     * Teen cheezein check hoti hain: naam, profile photo, phone number.
     * Har ek ke liye pehle DB, phir user store / JWT — kisi ek jagah mil jaaye
     * to "hai" maana jaata hai, kyunki Google login ke turant baad DB me kuch
     * fields khaali hoti hain par token me maujood hoti hain.
     */
    const headerFirstName = String(
        profileFields.name || user?.name || decodeTokenData?.name || ""
    )
        .trim()
        .split(/\s+/)[0];
    const hasName = Boolean(headerFirstName);
    const hasProfileImg = Boolean(
        String(
            profileFields.profile ||
            user?.profile ||
            decodeTokenData?.profile ||
            decodeTokenData?.profilePicture ||
            ""
        ).trim()
    );
    const hasPhone = Boolean(
        String(profileFields.phone || decodeTokenData?.phone || "").trim()
    );
    // `loaded` ka intezaar zaroori hai — bina uske pehle render pe teeno khaali
    // dikhte hain aur poore profile wale user ko bhi prompt mil jaata.
    const profileIncomplete =
        profileFields.loaded && !(hasName && hasProfileImg && hasPhone);

    useEffect(() => {
        if (!profileIncomplete) {
            setShowCompletePrompt(false);
            return;
        }
        // setInterval (setTimeout nahi) — dono states baari-baari chalti
        // rehti hain jab tak profile poora nahi ho jaata.
        const timer = setInterval(
            () => setShowCompletePrompt((prev) => !prev),
            NUDGE_INTERVAL_MS
        );
        return () => clearInterval(timer);
    }, [profileIncomplete]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            // Start with either JWT field, then replace it with the latest DB
            // value. Different auth versions use profile or profilePicture.
            const tokenProfile = resolveProfileUrl(
                decodeTokenData?.profile || decodeTokenData?.profilePicture,
            );
            if (!user?.profile && tokenProfile && !cancelled) {
                updateUser({ profile: tokenProfile });
            }

            if (!userIdFromToken || !token) return;
            try {
                const freshUser = await getUserDetails(userIdFromToken, token);
                // DB khaali ho to token wala (Google login ke turant baad DB me
                // nahi hota), dono khaali ho to KHAALI.
                const freshProfile =
                    resolveProfileUrl(freshUser?.profilePicture) || tokenProfile;
                const freshName = freshUser?.name || decodeTokenData?.name || "";
                /*
                 * Store ko IS user ke data se hamesha overwrite karo — khaali
                 * value se bhi. `user` store localStorage me persist hota hai:
                 * pehle sirf non-empty value likhte the, to jis account ka naam
                 * ya photo DB me khaali tha us pe PICHLE login wale ka naam /
                 * photo dikhta rehta tha (kisi aur ka profile).
                 */
                if (
                    !cancelled &&
                    (freshProfile !== (user?.profile || "") ||
                        freshName !== (user?.name || ""))
                ) {
                    updateUser({ profile: freshProfile, name: freshName });
                }
                // Header ko phone chahiye, aur user store me phone hai hi nahi
                // (usme sirf name / profile / subscriptionDetails hain). Isi
                // maujooda call se utha lete hain.
                if (!cancelled) {
                    setProfileFields({
                        loaded: true,
                        name: freshUser?.name || "",
                        phone: freshUser?.phone || "",
                        profile: freshProfile || "",
                    });
                }
            } catch (error) {
                console.warn(
                    "[useProfileGreeting] profile refresh failed:",
                    error?.message || error,
                );
                // Fetch fail hua to greeting ko block mat karo — jo token me
                // hai usi pe faisla kar lo.
                if (!cancelled) setProfileFields((p) => ({ ...p, loaded: true }));
            }
        })();

        return () => {
            cancelled = true;
        };
        // Re-fetch only when the authenticated user changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdFromToken, token]);

    const profileImg =
        user?.profile ||
        decodeTokenData?.profile ||
        decodeTokenData?.profilePicture ||
        "";

    return { headerFirstName, showCompletePrompt, profileImg };
};

export default useProfileGreeting;
